import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
  LiveRatesPe,
  PeruService,
  QuotePreviewPe,
  QuotePreviewItemPe,
  RailConfigPe,
} from '../../services/peru.service';

/** Estrategia de precio (una decisión): 'ESPEJO' = igualar a Vita; 'A' = comisión propia. */
interface PaisForm {
  estrategia: 'A' | 'ESPEJO';
  margenMinUsd: number;
  comisionPct: number;
  fijoUsd: number;
  motivo: string;
}

interface Avanzado {
  costoFijoLocal: number;
  modoTasa: 'AUTO' | 'MANUAL';
  tasaCostoManual: number | null;
  tasaManualVigenteHasta: string | null;
}

const DEFAULT_PAIS: PaisForm = {
  estrategia: 'A', // Perú arranca en Modelo A (determinístico); ESPEJO requiere quote público PE.
  margenMinUsd: 0,
  comisionPct: 1.49,
  fijoUsd: 1.16,
  motivo: '',
};

const DEFAULT_AVANZADO: Avanzado = {
  costoFijoLocal: 1.7, // PEN
  modoTasa: 'AUTO',
  tasaCostoManual: null,
  tasaManualVigenteHasta: null,
};

const PREVIEW_MONTOS = [100, 500, 1000];

export interface RailPeruConfigData {
  countryCode?: string; // 'PE'
  countryName?: string;
}

/**
 * MODAL "Rail Perú — Precio al cliente". Espejo simplificado de
 * `RailColombiaConfigComponent` (Perú tiene un solo riel `PE`, moneda PEN 2 dec.).
 * NO importa nada de Colombia — usa `PeruService`.
 */
@Component({
  selector: 'app-rail-peru-config',
  templateUrl: './rail-peru-config.component.html',
  styleUrls: ['./rail-peru-config.component.sass'],
})
export class RailPeruConfigComponent implements OnInit, OnDestroy {
  config: RailConfigPe | null = null;
  live: LiveRatesPe | null = null;

  pais: PaisForm = { ...DEFAULT_PAIS };
  avanzado: Avanzado = { ...DEFAULT_AVANZADO };
  mostrarAvanzado = false;

  preview: QuotePreviewPe | null = null;
  previewLoading = false;
  private previewTimer: any = null;
  private previewSeq = 0;

  loading = false;
  saving = false;
  error: string | null = null;
  okMsg: string | null = null;

  /** Master del rail PEN: si OFF, no se ofrece USD→PEN al cliente. */
  usdtPenOfrecido = false;

  constructor(
    private svc: PeruService,
    public dialogRef: MatDialogRef<RailPeruConfigComponent>,
    @Inject(MAT_DIALOG_DATA) public data: RailPeruConfigData,
  ) {}

  ngOnInit(): void {
    this.load();
  }

  ngOnDestroy(): void {
    if (this.previewTimer) clearTimeout(this.previewTimer);
  }

  async load(): Promise<void> {
    this.loading = true;
    this.error = null;
    try {
      const [config, live, railes] = await Promise.all([
        this.svc.getConfig(),
        this.svc.getLive(),
        this.svc.getRailesActivos().catch(() => null),
      ]);
      this.config = config?.PE ?? null;
      this.live = live;
      this.usdtPenOfrecido = railes?.pen?.usdt_pen === true;
      this.fromConfig(this.config);
      this.refreshPreview(true);
    } catch (e: any) {
      this.error = e?.message || 'No se pudo cargar la configuración';
    } finally {
      this.loading = false;
    }
  }

  private fromConfig(c: RailConfigPe | null): void {
    this.pais = {
      estrategia: c?.modoPricing === 'ESPEJO' ? 'ESPEJO' : 'A',
      margenMinUsd: Number(c?.margenMinUsd ?? DEFAULT_PAIS.margenMinUsd) || 0,
      comisionPct: Number(c?.comisionPct ?? DEFAULT_PAIS.comisionPct),
      fijoUsd: Number(c?.fijoUsd ?? DEFAULT_PAIS.fijoUsd),
      motivo: '',
    };
    this.avanzado = c
      ? {
          costoFijoLocal: Number(c.costoFijoCop) || 0,
          modoTasa: c.modoTasa,
          tasaCostoManual: c.tasaCostoManual != null ? Number(c.tasaCostoManual) : null,
          tasaManualVigenteHasta: c.tasaManualVigenteHasta
            ? c.tasaManualVigenteHasta.slice(0, 16)
            : null,
        }
      : { ...DEFAULT_AVANZADO };
  }

  onFormChange(): void {
    this.okMsg = null;
    this.refreshPreview();
  }

  refreshPreview(inmediato = false): void {
    if (this.previewTimer) clearTimeout(this.previewTimer);
    this.previewTimer = setTimeout(() => this.fetchPreview(), inmediato ? 0 : 600);
  }

  private async fetchPreview(): Promise<void> {
    const seq = ++this.previewSeq;
    this.previewLoading = true;
    try {
      const p = await this.svc.quotePreview({
        montos: PREVIEW_MONTOS,
        modoPricing: this.pais.estrategia,
        comisionPct: Number(this.pais.comisionPct) || 0,
        fijoUsd: Number(this.pais.fijoUsd) || 0,
        margenMinUsd: Math.max(0, Number(this.pais.margenMinUsd) || 0),
        costoFijoLocal: Number(this.avanzado.costoFijoLocal) || 0,
      });
      if (seq === this.previewSeq) this.preview = p;
    } catch {
      if (seq === this.previewSeq) this.preview = null;
    } finally {
      if (seq === this.previewSeq) this.previewLoading = false;
    }
  }

  async saveAll(): Promise<void> {
    if (this.avanzado.modoTasa === 'MANUAL') {
      if (!(Number(this.avanzado.tasaCostoManual) > 0) || !this.avanzado.tasaManualVigenteHasta) {
        this.error = 'MANUAL requiere tasa y vencimiento futuros (en Avanzado)';
        return;
      }
    }
    this.saving = true;
    this.error = null;
    this.okMsg = null;
    try {
      await this.svc.upsertConfig({
        rail: 'PE',
        comisionPct: Number(this.pais.comisionPct),
        fijoUsd: Number(this.pais.fijoUsd),
        costoFijoLocal: Number(this.avanzado.costoFijoLocal) || 0,
        modoPricing: this.pais.estrategia,
        margenMinUsd: Math.max(0, Number(this.pais.margenMinUsd) || 0),
        modoTasa: this.avanzado.modoTasa,
        tasaCostoManual:
          this.avanzado.modoTasa === 'MANUAL'
            ? Number(this.avanzado.tasaCostoManual)
            : undefined,
        tasaManualVigenteHasta:
          this.avanzado.modoTasa === 'MANUAL' && this.avanzado.tasaManualVigenteHasta
            ? new Date(this.avanzado.tasaManualVigenteHasta).toISOString()
            : undefined,
        motivo: this.pais.motivo || undefined,
      });
      await this.svc.setRailesActivos({ usdtPen: this.usdtPenOfrecido });
      this.okMsg = `Guardado (${this.pais.estrategia === 'ESPEJO' ? 'Igualar a Vita' : 'Comisión propia'}).`;
      await this.load();
    } catch (e: any) {
      this.error = e?.error?.message || e?.message || 'No se pudo guardar';
      await this.load();
    } finally {
      this.saving = false;
    }
  }

  close(): void {
    this.dialogRef.close();
  }

  // ── Helpers de vista ──
  get items(): QuotePreviewItemPe[] {
    return this.preview?.rails?.PE?.items ?? [];
  }
  recibeDe(it: QuotePreviewItemPe): number | null {
    return it?.quote?.resultado?.recibe ?? null;
  }
  gananciaDe(it: QuotePreviewItemPe): number | null {
    return it?.quote?.resultado?.gananciaUsd ?? null;
  }
  clampedDe(it: QuotePreviewItemPe): boolean {
    return !!it?.quote?.resultado?.clamped;
  }
  margenOkDe(it: QuotePreviewItemPe): boolean {
    return !!it?.quote?.margenOk;
  }
  difVsVita(it: QuotePreviewItemPe): number | null {
    const vita = it?.ofertaVitaLocal;
    const damos = it?.quote?.resultado?.recibe;
    if (vita == null || damos == null) return null;
    return damos - vita;
  }
  get espejoDegradado(): boolean {
    return this.items.some((i) => i?.quote?.espejoFallback);
  }
  get hayClamp(): boolean {
    return this.items.some((i) => i?.quote?.resultado?.clamped);
  }
  partner(): number | null {
    return this.live?.rails?.PE?.partnerUsdSell ?? null;
  }
  fee(): number | null {
    return this.live?.rails?.PE?.fixedCostLocal ?? null;
  }
  dias(): number | null {
    return this.live?.rails?.PE?.businessDays ?? null;
  }
}
